import { useEffect } from "react";

import { IgButton } from "@intelligems/ig-theme/IgButton";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import Dialog from "@mui/material/Dialog";

import {
  ProductPickerFilters,
  sameProductPickerFilters,
} from "@src/features/ai/components/WorkflowEditor/execution/panel/productPickSeed";
import {
  AI_USER_INPUT_PRODUCT_PICKER_TYPE,
  ShopifyEntityPick,
} from "@src/features/ai/components/WorkflowEditor/execution/panel/shopifyEntityPickerCopy";

import { ProductPicker } from "@src/components/MainApp/SharedProductPicker/ProductPicker";
import { useDispatch, useSelector } from "@src/store";
import {
  closeProductPicker,
  getProductPickerFilters,
  getProductPickerSelectedProducts,
  openProductPicker,
} from "@src/store/slices/productPicker";
import { isProductPickerOpen } from "@src/store/slices/productPicker/selectors/isProductPickerOpen";

interface ShopifyProductPickerDialogProps {
  multiSelect: boolean;
  /**
   * What the conversation settled — static filters plus earlier steps'
   * answers — as clearable picker filters (see `productPickSeedFilters`).
   */
  seedFilters: ProductPickerFilters;
  /**
   * The filters the merchant left the picker with last time, when they
   * differed from the seed. The picker reopens on these instead of the seed,
   * so an earlier answer changing never overwrites the merchant's own edit.
   */
  editedFilters: ProductPickerFilters | undefined;
  /** Products the conversation ruled out, left out of the list. */
  excludedProductIds: string[];
  open: boolean;
  /** Blocks Done — e.g. while the chat is streaming or an upload is settling. */
  saveDisabled?: boolean;
  onClose: () => void;
  onSave: (picked: ShopifyEntityPick[]) => void;
  /**
   * Called on every way out with the filters the merchant left, or
   * `undefined` when they match the seed (nothing of theirs to keep).
   */
  onFiltersLeft: (edited: ProductPickerFilters | undefined) => void;
}

/**
 * The picker a `product` shopify-pick question opens. Every flat kind is
 * picked inline from the card (see `ShopifyEntityCombobox`); products keep the
 * shared `ProductPicker`, whose images, variants and filters have no inline
 * equivalent.
 *
 * The product picker's open state lives in the store, keyed by type, so this
 * mirrors `open` into it: opening dispatches `openProductPicker` with the
 * pick's filters and exclusions, and any way out (Cancel, Done, the X,
 * backdrop) dispatches `closeProductPicker`, which also clears the running
 * selection and filters — so the filters the merchant left are reported
 * first. Products are always multi-select in the shared picker, so a
 * single-answer question is enforced at Done, the way the cart-test picker
 * caps its count.
 */
export const ShopifyProductPickerDialog = ({
  multiSelect,
  seedFilters,
  editedFilters,
  excludedProductIds,
  open,
  saveDisabled = false,
  onClose,
  onSave,
  onFiltersLeft,
}: ShopifyProductPickerDialogProps) => {
  const dispatch = useDispatch();
  const pickerOpen = useSelector(
    isProductPickerOpen(AI_USER_INPUT_PRODUCT_PICKER_TYPE)
  );
  const selectedProducts = useSelector(getProductPickerSelectedProducts);
  const filters = useSelector(getProductPickerFilters);

  useEffect(() => {
    if (open && !pickerOpen) {
      dispatch(
        openProductPicker({
          type: AI_USER_INPUT_PRODUCT_PICKER_TYPE,
          filters: editedFilters ?? seedFilters,
          excludedProductIds,
        })
      );
    }
  }, [
    open,
    pickerOpen,
    dispatch,
    editedFilters,
    seedFilters,
    excludedProductIds,
  ]);

  // Read from this render, before any close resets the store.
  const reportFilters = () =>
    onFiltersLeft(
      sameProductPickerFilters(filters, seedFilters) ? undefined : filters
    );

  const close = () => {
    reportFilters();
    dispatch(closeProductPicker());
    onClose();
  };
  const tooMany = !multiSelect && selectedProducts.length > 1;

  return (
    <Dialog
      disableEscapeKeyDown
      fullWidth
      maxWidth="lg"
      open={open && pickerOpen}
      sx={{ zIndex: 1401 }}
      onClose={close}
      slotProps={{ paper: { style: { maxHeight: "700px" } } }}
    >
      <ProductPicker
        loadVariants={false}
        // The header X closes the store picker itself; this closes the rest.
        onCustomCloseFn={() => {
          reportFilters();
          onClose();
        }}
        dialogActions={[
          ...(tooMany
            ? [
                <IgTypography key="cap" variant="caption" color="error">
                  Pick one product
                </IgTypography>,
              ]
            : []),
          <IgButton key="cancel" variant="text" onClick={close}>
            Cancel
          </IgButton>,
          <IgButton
            key="done"
            variant="contained"
            disabled={
              (!multiSelect && selectedProducts.length === 0) ||
              tooMany ||
              saveDisabled
            }
            onClick={() => {
              reportFilters();
              onSave(
                selectedProducts.map((product) => ({
                  id: product.id,
                  label: product.title,
                }))
              );
              dispatch(closeProductPicker());
            }}
          >
            Done
          </IgButton>,
        ]}
      />
    </Dialog>
  );
};
